import { useEffect, useState } from 'react';
import Layout from './components/Layout';
import Home from './pages/Home';
import Works from './pages/Works';
import WorkDetail from './pages/WorkDetail';
import About from './pages/About';
import Console from './pages/Console';
import TravelMap from './pages/TravelMap';
import Health from './pages/Health';
import Login from './pages/Login';
import Mochi from './pages/Mochi';
import CocoHome from './pages/CocoHome';
import { api, ApiError } from './lib/api';
import { clearSession, getSession, SESSION_CLEARED_EVENT, setSession as persistSession } from './lib/auth';
import { useT } from './i18n';
import type { Session } from './types/token';

export type PageKey =
  | 'home'
  | 'works'
  | 'about'
  | 'console'
  | 'cocohome'
  | 'travelMap'
  | 'health'
  | 'mochi';

const LOGOUT_FADE_MS = 300;

export default function App() {
  const t = useT();
  const [session, setSessionState] = useState<Session | null>(() => getSession());
  const [page, setPage] = useState<PageKey>(() => (getSession()?.kind === 'guest' ? 'about' : 'home'));
  const [selectedWorkId, setSelectedWorkId] = useState<string | null>(null);
  const [validating, setValidating] = useState<boolean>(() => getSession()?.kind === 'access');
  const [loggingOut, setLoggingOut] = useState(false);

  useEffect(() => {
    if (!session || session.kind !== 'access') {
      setValidating(false);
      return;
    }
    let cancelled = false;
    api
      .me()
      .then((fresh) => {
        if (cancelled) return;
        persistSession(fresh);
        setSessionState(fresh);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 401) {
          clearSession();
          setSessionState(null);
        }
      })
      .finally(() => {
        if (!cancelled) setValidating(false);
      });
    return () => {
      cancelled = true;
    };
    // run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handleSessionCleared = () => {
      setSessionState(null);
      setPage('home');
      setSelectedWorkId(null);
      setValidating(false);
    };
    window.addEventListener(SESSION_CLEARED_EVENT, handleSessionCleared);
    return () => window.removeEventListener(SESSION_CLEARED_EVENT, handleSessionCleared);
  }, []);

  function handleLogin(s: Session) {
    setSessionState(s);
    setPage(s.kind === 'guest' ? 'about' : 'home');
    setSelectedWorkId(null);
  }

  function handleLogout() {
    if (loggingOut) return;
    setLoggingOut(true);
    void api.logout().catch(() => undefined);
    window.setTimeout(() => {
      clearSession();
      setSessionState(null);
      setPage('home');
      setSelectedWorkId(null);
      setLoggingOut(false);
    }, LOGOUT_FADE_MS);
  }

  function handleNavigate(target: PageKey) {
    const adminTarget =
      target === 'console' || target === 'cocohome' || target === 'health' || target === 'mochi';
    if (session?.kind === 'guest' && target !== 'about') return;
    if (adminTarget && (session?.kind !== 'access' || session.role !== 'admin')) return;
    setPage(target);
    setSelectedWorkId(null);
  }

  if (validating) {
    return (
      <div className="min-h-screen flex items-center justify-center text-sm font-mono text-coffee/70">
        {t('common.loading')}
      </div>
    );
  }

  if (!session) {
    return <Login onLogin={handleLogin} />;
  }

  const visiblePage = session.kind === 'guest' ? 'about' : page;
  const transitionKey =
    visiblePage === 'works' && selectedWorkId ? `works:${selectedWorkId}` : visiblePage;

  return (
    <div className={loggingOut ? 'animate-fade-out' : ''}>
      <Layout
        current={visiblePage}
        onNavigate={handleNavigate}
        session={session}
        onLogout={handleLogout}
        transitionKey={transitionKey}
      >
        {visiblePage === 'home' && <Home onSeeWorks={() => setPage('works')} />}
        {visiblePage === 'works' && selectedWorkId === null && (
          <Works onSelectWork={setSelectedWorkId} />
        )}
        {visiblePage === 'works' && selectedWorkId !== null && (
          <WorkDetail
            id={selectedWorkId}
            onBack={() => setSelectedWorkId(null)}
          />
        )}
        {visiblePage === 'about' && <About />}
        {visiblePage === 'console' && session.kind === 'access' && session.role === 'admin' && (
          <Console session={session} />
        )}
        {visiblePage === 'cocohome' && session.kind === 'access' && session.role === 'admin' && (
          <CocoHome />
        )}
        {visiblePage === 'travelMap' && <TravelMap />}
        {visiblePage === 'health' && session.kind === 'access' && session.role === 'admin' && <Health />}
        {visiblePage === 'mochi' && session.kind === 'access' && session.role === 'admin' && <Mochi />}
      </Layout>
    </div>
  );
}
